import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { ChartsWrapperComponent } from '../../../../../common/components/charts-wrapper/charts-wrapper.component';
import {
  ChartItems,
  ChartType,
  DoughnutChartData,
  LineChartData,
} from '../../../../../common/components/charts-wrapper/charts.model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  completionDurationRangeFilterOptions,
  defaultRangeFilterOptions,
  SystemReports,
} from '../../../../../common/constants/reports';
import { WorkflowReportsService } from './workflow-reports.service';
import { RangeFilterDropdownComponent } from '../../../../../common/components/range-filter-dropdown/range-filter-dropdown.component';
import { ChartItemComponent } from '../../../../../common/components/chart-item/chart-item.component';
import { ReportService } from '../../../../../common/services/report/report.service';
import { ReportResponseData } from '../../../../../common/models/report.model';

@Component({
  selector: 'lib-reports-tab',
  imports: [
    ChartsWrapperComponent,
    TranslocoPipe,
    RangeFilterDropdownComponent,
    ChartItemComponent,
  ],
  templateUrl: './reports-tab.component.html',
  styleUrl: './reports-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ReportsTabComponent implements OnInit {
  private readonly workflowReportsService = inject(WorkflowReportsService);
  private readonly transloco = inject(TranslocoService);
  private readonly reportService = inject(ReportService);

  requestId = input.required<string | undefined>();
  active = input<boolean>(false);

  systemReports = SystemReports;

  // Requests per status chart
  requestsPerStatusChartData = signal<ReportResponseData | undefined>(
    undefined
  );
  requestsPerStatusChart = signal<DoughnutChartData | undefined>(undefined);
  requestsPerStatusChartItems = signal<ChartItems[]>([]);

  // Average Completion Duration vs. Workflow Total SLA
  averageCompletionDurationChartData = signal<ReportResponseData | undefined>(
    undefined
  );
  averageCompletionDurationChart = signal<LineChartData | undefined>(undefined);
  averageCompletionDurationChartItems = signal<ChartItems[]>([]);

  // Current Pending Tasks per Assignee
  currentPendingTasksPerAssigneeChartData = signal<
    ReportResponseData | undefined
  >(undefined);
  currentPendingTasksPerAssigneeChart = signal<DoughnutChartData | undefined>(
    undefined
  );
  currentPendingTasksPerAssigneeChartItems = signal<ChartItems[]>([]);

  // Completed Requests On-Time vs. Delayed
  completedRequestsOnTimeVsDelayedChartData = signal<
    ReportResponseData | undefined
  >(undefined);
  completedRequestsOnTimeVsDelayedChart = signal<DoughnutChartData | undefined>(
    undefined
  );
  completedRequestsOnTimeVsDelayedChartItems = signal<ChartItems[]>([]);

  // Range Filter
  toggleRangeFilter = signal(false);
  rangeFilterOptions = signal<{ label: string; value: string }[]>(
    defaultRangeFilterOptions
  );
  completionDurationRangeFilterOptions = signal<
    { label: string; value: string }[]
  >(completionDurationRangeFilterOptions);
  timeZoneLocation = Intl.DateTimeFormat().resolvedOptions().timeZone;
  selectedRequestsPerStatusRangeFilter = signal<{
    label: string;
    value: string;
  }>(this.rangeFilterOptions()[0]);
  selectedCompletionDurationRangeFilter = signal<{
    label: string;
    value: string;
  }>({ label: 'Last 180 Days', value: 'last_180_days' });
  selectedCurrentPendingTasksRangeFilter = signal<{
    label: string;
    value: string;
  }>(this.rangeFilterOptions()[0]);
  selectedCompletedRequestsOnTimeVsDelayedRangeFilter = signal<{
    label: string;
    value: string;
  }>(this.rangeFilterOptions()[0]);
  requestsPerStatusChartAnimation = signal(true);
  averageCompletionDurationChartAnimation = signal(true);
  currentPendingTasksPerAssigneeChartAnimation = signal(true);
  completedRequestsOnTimeVsDelayedChartAnimation = signal(true);

  constructor() {
    effect(() => {
      if (this.active()) {
        this.initRequestsPerStatusChart();
        this.initAverageCompletionDurationChart();
        this.initCurrentPendingTasksPerAssigneeChart();
        this.initCompletedRequestsOnTimeVsDelayedChart();
      } else {
        this.requestsPerStatusChart.set(undefined);
        this.averageCompletionDurationChart.set(undefined);
        this.currentPendingTasksPerAssigneeChart.set(undefined);
        this.completedRequestsOnTimeVsDelayedChart.set(undefined);
      }
    });
  }

  ngOnInit(): void {
    this.getRequestsPerStatusReport();
    this.getAverageCompletionDurationReport();
    this.getCurrentPendingTasksPerAssigneeReport();
    this.getCompletedRequestsOnTimeVsDelayedReport();
  }

  getRequestsPerStatusReport(): void {
    this.workflowReportsService
      .getWorkflowReports(
        this.requestId() as string,
        this.systemReports[0].key,
        this.timeZoneLocation,
        this.selectedRequestsPerStatusRangeFilter().value
      )
      .subscribe((response: { data: ReportResponseData }) => {
        this.requestsPerStatusChartData.set(response.data);
        this.initRequestsPerStatusChart();
      });
  }

  getAverageCompletionDurationReport(): void {
    this.workflowReportsService
      .getWorkflowReports(
        this.requestId() as string,
        this.systemReports[1].key,
        this.timeZoneLocation,
        this.selectedCompletionDurationRangeFilter().value
      )
      .subscribe((response: { data: ReportResponseData }) => {
        this.averageCompletionDurationChartData.set(response.data);
        this.initAverageCompletionDurationChart();
      });
  }

  getCurrentPendingTasksPerAssigneeReport(): void {
    this.workflowReportsService
      .getWorkflowReports(
        this.requestId() as string,
        this.systemReports[2].key,
        this.timeZoneLocation,
        this.selectedCurrentPendingTasksRangeFilter().value
      )
      .subscribe((response: { data: ReportResponseData }) => {
        this.currentPendingTasksPerAssigneeChartData.set(response.data);
        this.initCurrentPendingTasksPerAssigneeChart();
      });
  }

  getCompletedRequestsOnTimeVsDelayedReport(): void {
    this.workflowReportsService
      .getWorkflowReports(
        this.requestId() as string,
        this.systemReports[3].key,
        this.timeZoneLocation,
        this.selectedCompletedRequestsOnTimeVsDelayedRangeFilter().value
      )
      .subscribe((response: { data: ReportResponseData }) => {
        this.completedRequestsOnTimeVsDelayedChartData.set(response.data);
        this.initCompletedRequestsOnTimeVsDelayedChart();
      });
  }

  initRequestsPerStatusChart(): void {
    if (!this.requestsPerStatusChartData()) {
      return;
    }

    const chartData = this.requestsPerStatusChartData() as ReportResponseData;
    const total = this.reportService.sumArray(chartData.data.datasets[0].data);
    this.requestsPerStatusChart.set({
      type: chartData.type as ChartType,
      id: this.systemReports[0].key,
      data: {
        labels: chartData.data.labels.map(label => label.title),
        datasets: [
          {
            label: this.transloco.translate('reports_tab_requests_label'),
            data: chartData.data.datasets[0].data,
            backgroundColor: this.systemReports[0].backgroundColor,
            hoverOffset: 4,
          },
        ],
      },
      title: this.transloco.translate('reports_tab_total_requests_label'),
      value: total,
      cutout: this.reportService.getCurrentValueInPixels(3.75),
      height: this.reportService.getCurrentValueInPixels(10),
      width: this.reportService.getCurrentValueInPixels(10),
    });

    const chart = this.requestsPerStatusChart() as DoughnutChartData;
    this.requestsPerStatusChartItems.set(
      chart.data.datasets[0].data.map((value, index) => ({
        label: chart.data.labels[index],
        value,
        backgroundColor: chart.data.datasets[0].backgroundColor?.[index] ?? '',
        percentage: this.reportService.formatPercentage(value, chart.value),
      }))
    );
  }

  initAverageCompletionDurationChart(): void {
    if (!this.averageCompletionDurationChartData()) {
      return;
    }

    const chartData =
      this.averageCompletionDurationChartData() as ReportResponseData;
    this.averageCompletionDurationChart.set({
      type: chartData.type as ChartType,
      id: this.systemReports[1].key,
      data: {
        labels: chartData.data.labels.map(label => label.title),
        datasets: [
          {
            label: this.transloco.translate(
              'reports_tab_workflow_total_sla_label'
            ),
            data: chartData.data.datasets[0].data,
            backgroundColor: [this.systemReports[1].backgroundColor[0]],
            borderColor: [this.systemReports[1].backgroundColor[0]],
            fill: false,
          },
          {
            label: this.transloco.translate(
              'reports_tab_average_completion_duration_label'
            ),
            data: chartData.data.datasets[1].data,
            backgroundColor: [this.systemReports[1].backgroundColor[1]],
            borderColor: [this.systemReports[1].backgroundColor[1]],
            fill: false,
          },
        ],
      },
      height: 200,
      width: 0,
    });

    const chart = this.averageCompletionDurationChart() as LineChartData;
    this.averageCompletionDurationChartItems.set(
      chart.data.datasets.map(item => ({
        label: item.label,
        backgroundColor: item.backgroundColor?.[0] ?? '',
      }))
    );
  }

  initCurrentPendingTasksPerAssigneeChart(): void {
    if (!this.currentPendingTasksPerAssigneeChartData()) {
      return;
    }

    const chartData =
      this.currentPendingTasksPerAssigneeChartData() as ReportResponseData;
    const total = this.reportService.sumArray(chartData.data.datasets[0].data);
    this.currentPendingTasksPerAssigneeChart.set({
      type: chartData.type as ChartType,
      id: this.systemReports[2].key,
      data: {
        labels: chartData.data.labels.map(label => label.title),
        datasets: [
          {
            label: this.transloco.translate('reports_tab_pending_tasks_label'),
            data: chartData.data.datasets[0].data,
            backgroundColor: this.systemReports[2].backgroundColor,
            hoverOffset: 4,
          },
        ],
      },
      title: this.transloco.translate('reports_tab_total_pending_tasks_label'),
      value: total,
      cutout: this.reportService.getCurrentValueInPixels(3.75),
      height: this.reportService.getCurrentValueInPixels(10),
      width: this.reportService.getCurrentValueInPixels(10),
    });

    const chart =
      this.currentPendingTasksPerAssigneeChart() as DoughnutChartData;
    this.currentPendingTasksPerAssigneeChartItems.set(
      chart.data.datasets[0].data.map((value, index) => ({
        label: chart.data.labels[index],
        value,
        backgroundColor: chart.data.datasets[0].backgroundColor?.[index] ?? '',
        percentage: this.reportService.formatPercentage(value, chart.value),
        tooltipData: chartData.data.labels[index].subtitle ?? '',
      }))
    );
  }

  initCompletedRequestsOnTimeVsDelayedChart(): void {
    if (!this.completedRequestsOnTimeVsDelayedChartData()) {
      return;
    }

    const chartData =
      this.completedRequestsOnTimeVsDelayedChartData() as ReportResponseData;
    const total = this.reportService.sumArray(chartData.data.datasets[0].data);
    this.completedRequestsOnTimeVsDelayedChart.set({
      type: chartData.type as ChartType,
      id: this.systemReports[3].key,
      data: {
        labels: chartData.data.labels.map(label => label.title),
        datasets: [
          {
            label: this.transloco.translate(
              'reports_tab_completed_requests_label'
            ),
            data: chartData.data.datasets[0].data,
            backgroundColor: this.systemReports[3].backgroundColor,
            hoverOffset: 4,
          },
        ],
      },
      title: this.transloco.translate(
        'reports_tab_total_completed_requests_label'
      ),
      value: total,
      cutout: this.reportService.getCurrentValueInPixels(3.75),
      height: this.reportService.getCurrentValueInPixels(10),
      width: this.reportService.getCurrentValueInPixels(10),
    });

    const chart =
      this.completedRequestsOnTimeVsDelayedChart() as DoughnutChartData;
    this.completedRequestsOnTimeVsDelayedChartItems.set(
      chart.data.datasets[0].data.map((value, index) => ({
        label: chart.data.labels[index],
        value,
        backgroundColor: chart.data.datasets[0].backgroundColor?.[index] ?? '',
        percentage: this.reportService.formatPercentage(value, chart.value),
      }))
    );
  }

  selectRequestsPerStatusRangeFilter(option: {
    label: string;
    value: string;
  }): void {
    this.selectedRequestsPerStatusRangeFilter.set(option);
    this.requestsPerStatusChartAnimation.set(true);
    this.averageCompletionDurationChartAnimation.set(false);
    this.currentPendingTasksPerAssigneeChartAnimation.set(false);
    this.completedRequestsOnTimeVsDelayedChartAnimation.set(false);
    this.getRequestsPerStatusReport();
  }

  selectCompletionDurationRangeFilter(option: {
    label: string;
    value: string;
  }): void {
    this.selectedCompletionDurationRangeFilter.set(option);
    this.requestsPerStatusChartAnimation.set(false);
    this.averageCompletionDurationChartAnimation.set(true);
    this.currentPendingTasksPerAssigneeChartAnimation.set(false);
    this.completedRequestsOnTimeVsDelayedChartAnimation.set(false);
    this.getAverageCompletionDurationReport();
  }

  selectCompletedRequestsOnTimeVsDelayedRangeFilter(option: {
    label: string;
    value: string;
  }): void {
    this.selectedCompletedRequestsOnTimeVsDelayedRangeFilter.set(option);
    this.requestsPerStatusChartAnimation.set(false);
    this.averageCompletionDurationChartAnimation.set(false);
    this.currentPendingTasksPerAssigneeChartAnimation.set(false);
    this.completedRequestsOnTimeVsDelayedChartAnimation.set(true);
    this.getCompletedRequestsOnTimeVsDelayedReport();
  }
}
